import {
  cantonFixListQuerySchema,
  chargeFailureListQuerySchema,
  legacyMigrationListQuerySchema,
  rechargeWebhookEventListQuerySchema,
} from '@flux/shared';
import { createRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { SubscriptionsPage } from '@/features/subscriptions/subscriptions-page.js';
import { DEFAULT_SUBSCRIPTION_VIEW, SUBSCRIPTION_VIEWS } from '@/features/subscriptions/views.js';
import { authenticatedRoute } from './authenticated.js';

/**
 * `/abonnements` — écran Abonnements (`06` §8). `view` pilote l'onglet ; sa
 * liste vient de `features/subscriptions/views.ts` (un onglet inconnu, par
 * exemple un lien vers un onglet pas encore livré, retombe sur l'onglet par
 * défaut). Les filtres dérivent des schémas `@flux/shared` des endpoints
 * (`rechargeWebhookEventListQuerySchema` pour Webhooks : topic, outcome ;
 * `chargeFailureListQuerySchema` pour Paiements échoués : category, status,
 * search ; `cantonFixListQuerySchema` pour Cantons suisses : status ;
 * `legacyMigrationListQuerySchema` pour Migrations legacy : status, search ;
 * pagination, tri et période communs) ; `id` ouvre le panneau de détail
 * (`README-shell.md`, « Sheet porté par l'URL »). Un filtre propre à un
 * onglet mal formé dans l'URL est ignoré plutôt que de casser la page.
 *
 * `status` est partagé par trois onglets aux valeurs différentes : chaque
 * onglet relit ensuite le paramètre avec son propre schéma. Une liste vide
 * (`status=[]`) est conservée : Cantons suisses y lit le choix explicite
 * « Toutes » face à son défaut calculé.
 */
const subscriptionsSearchSchema = rechargeWebhookEventListQuerySchema.extend({
  category: chargeFailureListQuerySchema.shape.category.catch(undefined),
  status: z
    .union([
      chargeFailureListQuerySchema.shape.status,
      cantonFixListQuerySchema.shape.status,
      legacyMigrationListQuerySchema.shape.status,
    ])
    .catch(undefined),
  search: z
    .union([chargeFailureListQuerySchema.shape.search, legacyMigrationListQuerySchema.shape.search])
    .catch(undefined),
  view: z.enum(SUBSCRIPTION_VIEWS).default(DEFAULT_SUBSCRIPTION_VIEW).catch(DEFAULT_SUBSCRIPTION_VIEW),
  id: z.string().min(1).optional(),
});

export const abonnementsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/abonnements',
  validateSearch: (search: Record<string, unknown>) => subscriptionsSearchSchema.parse(search),
  component: SubscriptionsPage,
});
